import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Home, Phone } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MembershipSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [memberNumber, setMemberNumber] = useState(null);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/membership/payment-status/${sessionId}`
        );

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Try to get member number from registration
          // For now, we'll show success
        } else if (response.data.status === 'complete') {
          setStatus('success');
        } else {
          // Still processing, check again
          setTimeout(checkPaymentStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-orange-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <Loader2 className="h-16 w-16 mx-auto text-blue-600 animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-gray-700">Zahlung wird verarbeitet...</h2>
            <p className="text-gray-500 mt-2">Bitte warten Sie einen Moment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-orange-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <XCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700">Ein Fehler ist aufgetreten</h2>
            <p className="text-gray-500 mt-2 mb-6">
              Die Zahlung konnte nicht verifiziert werden. Bitte kontaktieren Sie uns.
            </p>
            <div className="space-y-3">
              <a href="tel:+4915739696942">
                <Button className="w-full">
                  <Phone className="h-4 w-4 mr-2" /> 01573 969 69 42 anrufen
                </Button>
              </a>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  <Home className="h-4 w-4 mr-2" /> Zur Startseite
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-orange-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-t-lg py-8">
          <CheckCircle className="h-20 w-20 mx-auto mb-4" />
          <CardTitle className="text-2xl">Willkommen bei der Pfotenflatrate!</CardTitle>
        </CardHeader>
        <CardContent className="py-8 space-y-6">
          <div className="text-center">
            <p className="text-lg text-gray-700">
              Ihre Zahlung war erfolgreich und Ihre Mitgliedschaft ist jetzt aktiv!
            </p>
          </div>

          {memberNumber && (
            <div className="bg-blue-50 rounded-xl p-6 text-center">
              <p className="text-sm text-blue-600 mb-1">Ihre Mitgliedsnummer:</p>
              <p className="text-4xl font-bold text-blue-700">#{memberNumber}</p>
            </div>
          )}

          <div className="bg-orange-50 rounded-xl p-6">
            <h3 className="font-semibold text-orange-800 mb-3">📧 Was passiert als Nächstes?</h3>
            <ul className="space-y-2 text-sm text-orange-700">
              <li>✓ Sie erhalten eine Bestätigungs-E-Mail mit allen Details</li>
              <li>✓ Ihre Mitgliedsnummer wird Ihnen per E-Mail zugesendet</li>
              <li>✓ Sie können ab sofort Termine buchen!</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="font-semibold text-gray-700 mb-3">🐾 Ihre Vorteile:</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• 3 Fahrten zum Tierarzt pro Jahr</li>
              <li>• 2 Fahrten zur Tierklinik pro Jahr</li>
              <li>• 1 Stunde Warteservice pro Fahrt inklusive</li>
              <li>• Bonus-Fahrt bei Nicht-Nutzung der Tierklinik-Fahrten</li>
            </ul>
          </div>

          <div className="flex flex-col gap-3">
            <a href="tel:+4915739696942">
              <Button className="w-full bg-gradient-to-r from-blue-600 to-orange-500">
                <Phone className="h-4 w-4 mr-2" /> Jetzt Termin vereinbaren
              </Button>
            </a>
            <Link to="/">
              <Button variant="outline" className="w-full">
                <Home className="h-4 w-4 mr-2" /> Zur Startseite
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MembershipSuccess;
